import React, { Component } from "react";
import { Button, Alert, Card, Badge, ProgressBar } from "react-bootstrap";
import configData from "../../../config.json";

export default class ValidationStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      previewData: [],
      validationSummary: null,
      loading: false,
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    this.validateData();
  }

  validateData = () => {
    this.setState({ loading: true });

    // Check if we have a valid session ID
    const dbSessionId = this.props.session.sessionId || this.props.session.session_id;
    if (!dbSessionId) {
      this.setState({ loading: false });
      this.props.onError("No session ID found. Please go back and upload a file first.");
      return;
    }

    const formData = new FormData();
    // The session ID from the upload step (database import session ID)
    formData.append("session_id", dbSessionId);
    // These are for user authentication  
    formData.append("sId", sessionStorage.getItem("sessionId"));
    formData.append("uId", sessionStorage.getItem("uId"));
    
    console.log("ValidationStep - FULL Session object:", JSON.stringify(this.props.session, null, 2));
    console.log("ValidationStep - Import session ID (database):", dbSessionId);
    console.log("ValidationStep - User auth session ID:", sessionStorage.getItem("sessionId"));
    console.log("ValidationStep - User ID:", sessionStorage.getItem("uId"));
    
    // Also log all the properties of the session object
    if (this.props.session) {
      console.log("ValidationStep - Session properties:", Object.keys(this.props.session));
      console.log("ValidationStep - sessionId property:", this.props.session.sessionId);
      console.log("ValidationStep - session_id property:", this.props.session.session_id);
      console.log("ValidationStep - importSessionId property:", this.props.session.importSessionId);
    }

    fetch(configData.api_url + "C_BulkImport/preview", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        this.setState({ loading: false });

        if (json.status === "success") {
          const previewData = json.preview_data || [];

          this.setState({
            previewData,
            validationSummary: this.calculateValidationSummary(previewData),
          });

          this.props.onUpdate({
            validationResults: previewData,
            status: "validated",
          });
        } else {
          this.props.onError(json.message || "Failed to validate data");
        }
      })
      .catch((error) => {
        console.error("Validation API Error:", error);
        this.setState({ loading: false });
        this.props.onError("Network error occurred while validating data: " + error.message);
      });
  };

  calculateValidationSummary = (previewData) => {
    const summary = {
      total: previewData.length,
      valid: 0,
      warnings: 0,
      errors: 0,
      commonErrors: {},
    };

    previewData.forEach((row) => {
      if (row.is_valid) {
        if (row.warnings && row.warnings.length > 0) {
          summary.warnings++;
        } else {
          summary.valid++;
        }
      } else {
        summary.errors++;

        // Count common errors
        if (row.errors) {
          Object.values(row.errors).forEach((fieldErrors) => {
            fieldErrors.forEach((error) => {
              const errorType = error.type || "unknown";
              summary.commonErrors[errorType] =
                (summary.commonErrors[errorType] || 0) + 1;
            });
          });
        }
      }
    });

    return summary;
  };

  proceedToProcessing = (importMode = 'valid_only') => {
    const { validationSummary } = this.state;

    // Update session with import mode preference
    this.props.onUpdate({
      importMode: importMode, // 'valid_only', 'force_all', 'skip_import'
      status: "validated",
    });

    let message = "";
    switch(importMode) {
      case 'valid_only':
        message = `Proceeding with ${validationSummary?.valid || 0} valid rows only.`;
        break;
      case 'force_all':
        message = `Proceeding with all ${validationSummary?.total || 0} rows (including invalid data).`;
        break;
      case 'skip_import':
        message = "Import cancelled. You can fix the data and try again.";
        return; // Don't proceed to next step
    }

    this.props.onSuccess(message);
    this.props.onNext();
  };

  renderValidationSummary = () => {
    const { validationSummary } = this.state;

    if (!validationSummary) return null;

    const { total, valid, warnings, errors } = validationSummary;
    const successRate = total > 0 ? Math.round((valid / total) * 100) : 0;
    const warningRate = total > 0 ? Math.round((warnings / total) * 100) : 0;
    const errorRate = total > 0 ? Math.round((errors / total) * 100) : 0;

    return (
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0">
            <i className="fas fa-chart-bar me-2"></i>
            Validation Summary
          </h6>
        </Card.Header>
        <Card.Body>
          <div className="row mb-3">
            <div className="col-md-3 text-center">
              <div className="h4 text-primary mb-1">{total}</div>
              <div className="small text-muted">Total Rows</div>
            </div>
            <div className="col-md-3 text-center">
              <div className="h4 text-success mb-1">{valid}</div>
              <div className="small text-muted">Valid ({successRate}%)</div>
            </div>
            <div className="col-md-3 text-center">
              <div className="h4 text-warning mb-1">{warnings}</div>
              <div className="small text-muted">Warnings ({warningRate}%)</div>
            </div>
            <div className="col-md-3 text-center">
              <div className="h4 text-danger mb-1">{errors}</div>
              <div className="small text-muted">Errors ({errorRate}%)</div>
            </div>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <span className="small">Success Rate</span>
              <span className="small">{successRate}%</span>
            </div>
            <ProgressBar>
              <ProgressBar variant="success" now={successRate} />
              <ProgressBar variant="warning" now={warningRate} />
              <ProgressBar variant="danger" now={errorRate} />
            </ProgressBar>
          </div>

          {Object.keys(validationSummary.commonErrors).length > 0 && (
            <div>
              <h6 className="mb-2">Common Errors</h6>
              <div className="d-flex flex-wrap gap-2">
                {Object.entries(validationSummary.commonErrors)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([errorType, count]) => (
                    <Badge key={errorType} bg="danger">
                      {this.formatErrorType(errorType)}: {count}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  formatErrorType = (errorType) => {
    const typeMap = {
      required: "Required Field",
      invalid_email: "Invalid Email",
      invalid_date: "Invalid Date",
      invalid_number: "Invalid Number",
      too_long: "Too Long",
      duplicate: "Duplicate",
      foreign_key: "Invalid Reference",
    };

    return typeMap[errorType] || errorType.replace("_", " ").toUpperCase();
  };

  renderPreviewRow = (row, index) => {
    const rowClass = row.is_valid
      ? row.warnings && row.warnings.length > 0
        ? "warning"
        : "valid"
      : "error";

    const hasErrors = row.errors && Object.keys(row.errors).length > 0;
    const hasWarnings = row.warnings && Object.keys(row.warnings).length > 0;

    return (
      <div key={index} className={`preview-row ${rowClass}`}>
        <div className="row-number">#{row.row_number}</div>

        <div className="row-status">
          {row.is_valid ? (
            hasWarnings ? (
              <i
                className="fas fa-exclamation-triangle text-warning"
                title="Has warnings"
              ></i>
            ) : (
              <i className="fas fa-check-circle text-success" title="Valid"></i>
            )
          ) : (
            <i
              className="fas fa-times-circle text-danger"
              title="Has errors"
            ></i>
          )}
        </div>

        <div className="row-data">
          {Object.entries(row.data)
            .slice(0, 4)
            .map(([field, value]) => (
              <div key={field} className="data-cell">
                <div className="small text-muted">{field}</div>
                <div>{value || "-"}</div>
              </div>
            ))}
          {Object.keys(row.data).length > 4 && (
            <div className="data-cell">
              <div className="small text-muted">...</div>
              <div>+{Object.keys(row.data).length - 4} more</div>
            </div>
          )}
        </div>

        {(hasErrors || hasWarnings) && (
          <div className="w-100 mt-2">
            {hasErrors && (
              <div className="error-list">
                <strong className="text-danger">Errors:</strong>
                {Object.entries(row.errors).map(([field, fieldErrors]) =>
                  fieldErrors.map((error, idx) => (
                    <div key={`${field}-${idx}`} className="error-item">
                      <div className="error-header">
                        <strong>{field}:</strong> {error.message || error}
                      </div>
                      {error.field_info && (
                        <div className="small text-muted">
                          Field type: {error.field_info}
                        </div>
                      )}
                      {error.fix && (
                        <div className="small text-info">
                          <i className="fas fa-lightbulb me-1"></i>
                          {error.fix}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {hasWarnings && (
              <div className="error-list">
                <strong className="text-warning">Warnings:</strong>
                {Object.entries(row.warnings).map(([field, fieldWarnings]) =>
                  fieldWarnings.map((warning, idx) => (
                    <div
                      key={`${field}-${idx}`}
                      className="error-item text-warning"
                    >
                      <strong>{field}:</strong> {warning.message || warning}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  renderDataPreview = () => {
    const { previewData, currentPage, pageSize } = this.state;

    if (previewData.length === 0) {
      return (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          No preview data available. The file might be empty or contain only
          headers.
        </Alert>
      );
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, previewData.length);
    const visibleRows = previewData.slice(startIndex, endIndex);

    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-eye me-2"></i>
              Data Preview ({startIndex + 1}-{endIndex} of {previewData.length})
            </h6>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  this.setState({ currentPage: Math.max(1, currentPage - 1) })
                }
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i>
              </Button>
              <span className="px-2 py-1 small">
                Page {currentPage} of {Math.ceil(previewData.length / pageSize)}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  this.setState({
                    currentPage: Math.min(
                      Math.ceil(previewData.length / pageSize),
                      currentPage + 1
                    ),
                  })
                }
                disabled={
                  currentPage >= Math.ceil(previewData.length / pageSize)
                }
              >
                <i className="fas fa-chevron-right"></i>
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="preview-container">
            {visibleRows.map((row, index) =>
              this.renderPreviewRow(row, startIndex + index)
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  render() {
    const { loading, validationSummary } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="mt-3">
            <h5>Validating Your Data</h5>
            <p className="text-muted">
              Checking data types, required fields, and business rules...
            </p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="mb-2">Data Validation</h4>
            <p className="text-muted">
              Review the validation results below. Rows with errors will be
              skipped during import.
            </p>
          </div>
        </div>

        {this.renderValidationSummary()}

        {this.renderDataPreview()}

        {validationSummary && validationSummary.errors > 0 && (
          <Alert variant="warning" className="mt-4">
            <h6 className="alert-heading">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Validation Issues Found
            </h6>
            <p className="mb-2">
              {validationSummary.errors} rows have validation errors and will be
              skipped during import. Common issues include:
            </p>
            <ul className="mb-0">
              <li>Missing required fields</li>
              <li>Invalid data formats (dates, emails, numbers)</li>
              <li>Text too long for database fields</li>
              <li>Invalid references to other tables</li>
            </ul>
          </Alert>
        )}

        {/* Import Decision Section */}
        {validationSummary && (
          <Card className="mt-4 border-primary">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-decision me-2"></i>
                Choose Your Import Strategy
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                {/* Option 1: Import Valid Data Only */}
                <div className="col-md-4 mb-3">
                  <Card className={`h-100 border-success ${validationSummary.valid === 0 ? 'opacity-50' : ''}`}>
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <i className="fas fa-check-circle fa-3x text-success"></i>
                      </div>
                      <h5 className="text-success">Import Valid Data</h5>
                      <p className="small text-muted">
                        Import only rows that passed validation
                      </p>
                      <div className="h4 text-success mb-2">{validationSummary.valid}</div>
                      <div className="small text-muted mb-3">Valid Rows</div>
                      <Button
                        variant="success"
                        size="lg"
                        onClick={() => this.proceedToProcessing('valid_only')}
                        disabled={validationSummary.valid === 0}
                        className="w-100"
                      >
                        <i className="fas fa-play me-2"></i>
                        Import Valid Only
                      </Button>
                    </Card.Body>
                  </Card>
                </div>

                {/* Option 2: Force Import All Data */}
                <div className="col-md-4 mb-3">
                  <Card className="h-100 border-warning">
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <i className="fas fa-exclamation-triangle fa-3x text-warning"></i>
                      </div>
                      <h5 className="text-warning">Force Import All</h5>
                      <p className="small text-muted">
                        Import all rows, including invalid ones
                      </p>
                      <div className="h4 text-warning mb-2">{validationSummary.total}</div>
                      <div className="small text-muted mb-3">Total Rows</div>
                      <Button
                        variant="warning"
                        size="lg"
                        onClick={() => this.proceedToProcessing('force_all')}
                        className="w-100"
                        title="Invalid rows may cause database errors or be skipped"
                      >
                        <i className="fas fa-bolt me-2"></i>
                        Force Import All
                      </Button>
                    </Card.Body>
                  </Card>
                </div>

                {/* Option 3: Skip Import */}
                <div className="col-md-4 mb-3">
                  <Card className="h-100 border-danger">
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <i className="fas fa-times-circle fa-3x text-danger"></i>
                      </div>
                      <h5 className="text-danger">Skip Import</h5>
                      <p className="small text-muted">
                        Don't import data, fix issues first
                      </p>
                      <div className="h4 text-danger mb-2">{validationSummary.errors}</div>
                      <div className="small text-muted mb-3">Error Rows</div>
                      <Button
                        variant="outline-danger"
                        size="lg"
                        onClick={() => this.proceedToProcessing('skip_import')}
                        className="w-100"
                      >
                        <i className="fas fa-ban me-2"></i>
                        Skip & Fix Data
                      </Button>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              {/* Additional Actions */}
              <div className="text-center mt-3 pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={this.validateData}
                  className="me-3"
                >
                  <i className="fas fa-refresh me-2"></i>
                  Re-validate Data
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => window.history.back()}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Go Back to Mapping
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Fallback for when validation data is not available */}
        {!validationSummary && !this.state.loading && (
          <div className="text-center mt-4">
            <Alert variant="info" className="mb-4">
              <h6 className="alert-heading">
                <i className="fas fa-info-circle me-2"></i>
                Validation Data Not Available
              </h6>
              <p className="mb-0">
                Unable to load validation results. You can proceed anyway or go back to fix the mapping.
              </p>
            </Alert>
            
            <div className="d-flex justify-content-center gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={() => this.proceedToProcessing('force_all')}
                className="btn-primary-custom"
              >
                <i className="fas fa-forward me-2"></i>
                Proceed Anyway
              </Button>
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={() => window.history.back()}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Go Back
              </Button>
            </div>
          </div>
        )}

        {validationSummary && validationSummary.valid === 0 && (
          <Alert variant="danger" className="mt-4 text-center">
            <h5 className="alert-heading">
              <i className="fas fa-times-circle me-2"></i>
              No Valid Rows Found
            </h5>
            <p className="mb-3">
              All rows in your file have validation errors. Please fix the data
              and re-upload the file.
            </p>
            <Button
              variant="outline-danger"
              onClick={() => window.history.back()}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Go Back to Fix Mappings
            </Button>
          </Alert>
        )}

        {/* Help Information */}
        <div className="row mt-4">
          <div className="col-12">
            <Alert variant="light" className="border">
              <h6 className="alert-heading">
                <i className="fas fa-lightbulb me-2"></i>
                Understanding Validation Results
              </h6>
              <div className="row">
                <div className="col-md-4">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    <strong>Valid:</strong> Ready for import
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                    <strong>Warning:</strong> Will import with caution
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-times-circle text-danger me-2"></i>
                    <strong>Error:</strong> Will be skipped
                  </div>
                </div>
              </div>
            </Alert>
          </div>
        </div>
      </div>
    );
  }
}
